'use client'

import Image from 'next/image'
import Link from 'next/link'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export type RecipeRatePromptRecipe = {
  slug: string
  recipe_id?: number | null
  recipe_name?: string | null
  resourcePath?: string | null
  recipe_img_url?: string | null
  thumbnailPath?: string | null
  author_name?: string | null
  publisherName?: string | null
  publisherAvatar?: string | null
}

interface RecipeRatePromptModalProps {
  open: boolean
  recipe: RecipeRatePromptRecipe
  onSkip: () => void
  onRate: () => void
  /** Close without snoozing — e.g. click recipe card to return to the page. */
  onDismissWithoutSnooze: () => void
}

export function RecipeRatePromptModal({
  open,
  recipe,
  onSkip,
  onRate,
  onDismissWithoutSnooze,
}: RecipeRatePromptModalProps) {
  const thumb =
    recipe.resourcePath ||
    recipe.thumbnailPath ||
    recipe.recipe_img_url ||
    '/assets/icons/Recipe.svg'
  const recipeHref = `/recipes/${recipe.slug}`
  const name = recipe.recipe_name || 'this recipe'

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onSkip()
      }}
    >
      <DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] max-w-[520px] overflow-y-auto rounded-3xl border-0 p-6 shadow-[0px_0px_25px_rgba(0,0,0,0.15)] sm:p-8">
        <DialogHeader className="items-center space-y-0 text-center sm:text-center">
          <div className="mx-auto mb-5 flex justify-center">
            <Image
              src="/assets/icons/recipe-rate-prompt.png"
              alt=""
              width={115}
              height={115}
              className="h-[98px] w-[98px] object-contain sm:h-[115px] sm:w-[115px]"
              priority
            />
          </div>
          <DialogTitle className="text-center text-2xl font-semibold text-[#202020]">
            How did it turn out?
          </DialogTitle>
          <DialogDescription className="mt-3 text-center text-base leading-relaxed text-[#6B6B6B]">
            Made this recipe? Tell us what you thought. Your feedback helps make
            Culineer recipes better for everyone.
          </DialogDescription>
        </DialogHeader>

        <Link
          href={recipeHref}
          onClick={(e) => {
            e.preventDefault()
            onDismissWithoutSnooze()
          }}
          className="mt-6 flex items-center gap-3 rounded-xl bg-[#F7F7F7] p-3 transition-colors hover:bg-[#F0F0F0]"
        >
          <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-white">
            <img src={thumb} alt="" className="h-full w-full object-cover" />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <p className="truncate text-base font-semibold text-[#202020]">{name}</p>
            {recipe.publisherName && (
              <div className="mt-1 flex items-center gap-2">
                <img
                  src={recipe.publisherAvatar || '/assets/avatar/user-default.svg'}
                  alt=""
                  className="h-6 w-6 rounded-full object-cover"
                />
                <span className="truncate text-sm text-[#6B6B6B]">
                  {recipe.publisherName}
                </span>
              </div>
            )}
            {recipe.author_name && (
              <p className="mt-0.5 truncate text-sm text-[#6B6B6B]">
                Author: {recipe.author_name}
              </p>
            )}
          </div>
        </Link>

        <div className="mt-8 flex flex-col-reverse items-stretch justify-center gap-3 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={onSkip}
            className="inline-flex h-[43px] items-center justify-center rounded-full bg-[#E6E6E6] px-6 text-base font-medium text-[#202020] hover:bg-[#D8D8D8]"
          >
            Skip for now
          </button>
          <button
            type="button"
            onClick={onRate}
            className="inline-flex h-[43px] items-center justify-center rounded-full bg-[#85C349] px-6 text-base font-medium text-white hover:bg-[#76b03d]"
          >
            Rate the Recipe
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
